/* istanbul ignore file */

// This is new code that is hidden behind the platform-editor-use-atlassian-confluence-presets feature flag
// and it is not used in the tests, because tests are not running with the feature flag enabled.
// The coverage will be good, wnen the feature flag will be removed.
// Temove `/* istanbul ignore file */` when the platform-editor-use-atlassian-confluence-presets feature flag is removed.

import type { FeatureFlags } from '@atlaskit/editor-common/types';
import type { ScrollGutterPluginOptions } from '@atlaskit/editor-plugin-base';
import { shouldForceTracking } from '@atlaskit/editor-common/utils';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { forgeEditorPlugin } from '@atlassian/xen-editor-provider';
import { fullPagePreset } from '@atlassian/confluence-presets/full-page';

import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage';
import { fg } from '@confluence/feature-gating';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import {
	createInlineCommentCompoundExperience,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker';

import { createFeatureFlagsFromProps } from '../utils/createFeatureFlagsFromProps';

import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin';
import { ssrPlugin } from './plugins/ssrPlugin';
import { emptyStatePlugin } from './plugins/emptyStatePlugin';
import { conflictResolutionUIPlugin } from './plugins/conflictResolutionUIPlugin';
import type { FullPageEditorPresetOptions } from './createFullPageEditorPreset';

export function createFullPageEditorPresetNext(options: FullPageEditorPresetOptions) {
	const appearance = options.appearance;
	const prevAppearance = options.prevAppearance;
	const featureFlags: FeatureFlags = createFeatureFlagsFromProps(options.featureFlags);
	const cardOptions = options.linking?.smartLinks;
	const getEditorFeatureFlags = () => featureFlags;

	return fullPagePreset({
		pluginOptions: {
			paste: {
				cardOptions,
			},
			base: {
				allowScrollGutter: options.removeScrollGutter
					? undefined
					: ({
							getScrollElement: () => document.querySelector('.fabric-editor-popup-scroll-parent'),
						} as ScrollGutterPluginOptions),
			},
			hyperlink: {
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				lpLinkPicker: featureFlags.lpLinkPicker ?? false,
				onClickCallback: cardOptions?.onClickCallback,
			},
			quickInsert: {
				enableElementBrowser: options.elementBrowser?.showModal,
				elementBrowserHelpUrl: options.elementBrowser?.helpUrl,
				emptyStateHandler: options.elementBrowser?.emptyStateHandler,
				prioritySortingFn:
					typeof options.quickInsert === 'object'
						? options.quickInsert.prioritySortingFn
						: undefined,
				onInsert:
					typeof options.quickInsert === 'object' ? options.quickInsert.onInsert : undefined,
			},
			placeholder: {
				placeholder: options.placeholder,
			},
			submitEditor: options.onSave,
			selection: {
				useLongPressSelection: false,
				__livePage: options.__livePage,
			},
			breakout: {
				allowBreakoutButton: appearance === 'full-page',
			},
			expand: {
				appearance,
				__livePage: options.__livePage,
			},
			annotation: fg('confluence_comments_select_comment_experience')
				? options.annotationProviders
					? {
							...options.annotationProviders,
							createCommentExperience: createInlineCommentCompoundExperience,
							selectCommentExperience: {
								selectAnnotation: {
									complete: (annotationId: string) => {
										selectInlineCommentCompoundExperience.selectAnnotation.complete({
											pageClass: 'editor',
											annotationId,
											entryPoint: 'annotation-change',
										});
									},
								},
							},
						}
					: undefined
				: options.annotationProviders
					? {
							inlineComment: options.annotationProviders.inlineComment,
							createCommentExperience: createInlineCommentCompoundExperience,
						}
					: undefined,
			media: {
				...options.media, // values are from next/packages/editor-features/src/utils/mediaOptions.ts
				allowAdvancedToolBarOptions:
					typeof options.media?.allowAdvancedToolBarOptions !== 'undefined'
						? options.media?.allowAdvancedToolBarOptions
						: true,
				fullWidthEnabled: appearance === 'full-width',
				editorAppearance: appearance,
				waitForMediaUpload: options.waitForMediaUpload,
				alignLeftOnInsert:
					typeof options.media?.alignLeftOnInsert !== 'undefined'
						? options.media?.alignLeftOnInsert
						: false,
				getEditorFeatureFlags,
			},
			mentions: {
				handleMentionsChanged: options.handleMentionsChanged,
				mentionProvider: options.mentionProvider,
			},
			table: {
				tableOptions: {
					allowTableResizing: true,
					allowTableAlignment: true,
					allowBackgroundColor: true,
					allowColumnResizing: true,
					allowColumnSorting: true,
					allowDistributeColumns: true,
					allowHeaderColumn: true,
					allowHeaderRow: true,
					allowMergeCells: true,
					allowNumberColumn: true,
					allowNestedTables: true,
					allowControls: true,
					stickyHeaders: true,
					allowAddColumnWithCustomStep: false,
				},
				dragAndDropEnabled: featureFlags?.tableDragAndDrop,
				fullWidthEnabled: appearance === 'full-width',
				wasFullWidthEnabled: prevAppearance && prevAppearance === 'full-width',
				getEditorFeatureFlags,
			},
			tasksAndDecisions: {
				taskDecisionProvider: options.taskDecisionProvider,
				hasEditPermission: options.hasEditPermission,
				requestToEditContent: options.requestToEditContent,
				hasRequestedEditPermission: options.hasRequestedEditPermission,
			},
			collabEdit: {
				...options.collabEdit,
				EXPERIMENTAL_allowInternalErrorAnalytics:
					options.collabEdit?.EXPERIMENTAL_allowInternalErrorAnalytics ?? shouldForceTracking(),
				hideTelecursorOnLoad:
					options.__livePage && fg('platform_editor_no_telecursor_on_live_doc_init'),
			},
			contextPanel: fg('platform_editor_ai_object_sidebar_injection')
				? options.contextPanelOptions
				: undefined,
			extension: {
				breakoutEnabled: appearance === 'full-page',
				extensionHandlers: options.extensionHandlers,
				appearance,
				__rendererExtensionOptions: options.__rendererExtensionOptions,
			},
			layout: {
				editorAppearance: appearance,
			},
			card: {
				...options.linking?.smartLinks,
				fullWidthMode: appearance === 'full-width',
				linkPicker: options.linking?.linkPicker,
				lpLinkPicker: featureFlags.lpLinkPicker ?? false,
				editorAppearance: appearance,
				// @ts-ignore Temporary solution to check for Live Page editor.
				__livePage: options.__livePage,
			},
			insertBlock: {
				tableSelectorSupported: featureFlags?.tableSelector,
				appearance,
			},
			avatarGroup: {
				// Avatars are moved to Confluence codebase for Edit in Context
				// When Edit in Context is enabled primaryToolbarComponents is undefined
				// For more details please check
				// https://hello.atlassian.net/wiki/spaces/PCG/pages/2851572180/Editor+toolbar+for+live+pages+and+edit+in+context+projects
				collabEdit: options.collabEdit,
			},
			codeBidiWarning: {
				appearance,
			},
			referentiality: {
				...options.referentialityContext.options,
				api: options.referentialityContext.api ?? undefined,
			},
			ai: options.editorPluginAIOptions,
			loom: options.loomPluginOptions,
			editorViewMode: editorExperiment('live_pages_graceful_edit', 'control')
				? { mode: options.viewMode }
				: options.initialPluginConfiguration.viewModePlugin,
			aiDefinitions: options.editorPluginAIDefinitionsConfig,
			aiProactive: options.editorPluginAIProactiveConfig,
			selectionMarker: fg('platform_editor_no_cursor_on_live_doc_init')
				? { hideCursorOnInit: options.__livePage }
				: undefined,
			highlight: {
				textHighlightingFloatingToolbarExperiment:
					options.textHighlightingFloatingToolbarExperiment,
			},
			engagementPlatform: options.editorEngagementPlatformPluginConfig,
		},
		providers: {
			userPreferencesProvider: options.userPreferencesProvider,
			cardProvider: options.linking?.smartLinks?.provider,
			contextIdentifierProvider: options.contextIdentifierProvider,
			inlineCommentAnnotationProvider: options.annotationProviders?.inlineComment,
			mediaProvider: options.media?.provider,
			mentionProvider: options.mentionProvider,
			profilecardProvider: options.mention?.profilecardProvider,
			emojiProvider: options.emojiProvider,
			collabEditProvider: options.collabEdit?.provider,
			autoformattingProvider: options.autoformattingProvider,
		},
		enabledOptionalPlugins: {
			findReplace: Boolean(options.allowFindReplace),
			referentiality: Boolean(options.referentialityContext.plugin),
			loom: options.isLoomPluginEnabled,
			aiDefinitions: fg('confluence_ai_access_policy')
				? true
				: options.editorPluginAIDefinitionsEnabled,
			aiProactive: options.editorPluginAIProactiveEnabled,
			connectivity:
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			metrics: fg('platform_editor_ease_of_use_metrics'),
			codeBlockAdvanced: expVal('platform_editor_advanced_code_blocks', 'isEnabled', false),
			selectionExtension: expValEquals('platform_editor_controls', 'cohort', 'variant1'),
			aiExperience: Boolean(options.editorPluginAIOptions.aiGlobalOptIn.status !== 'disabled'),
		},
	})
		.add(ssrPlugin)
		.maybeAdd(
			[
				EditorHeadingLinksPlugin,
				{
					getViewPagePath: options.getViewPagePath,
				},
			],
			options.isHeadingLinksEnabled,
		)
		.add(pageTitleUtilsPlugin)
		.add(fabricExtensionManagerPlugin)
		.add(forgeEditorPlugin)
		.maybeAdd(
			editorOfflineStoragePlugin,
			options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
		)
		.maybeAdd(conflictResolutionUIPlugin, fg('platform_editor_offline_conflict_resolution'))
		.add(emptyStatePlugin)
		.maybeAdd(legacyContentMacroPlugin, fg('platform_editor_legacy_content_macro'));
}
